import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useModalDismiss } from './use-modal-dismiss';
import { cn } from '@/lib/utils';
import {
  AdminRequestError,
  EVENT_STREAMS,
  fetchDialerSettings,
  transportMapsEqual,
  updateEventTransport,
  type EventStream,
  type EventTransport,
} from './api';

// What each stream carries, in the operator's vocabulary — shown beside the
// toggle so a flip never needs the runbook open. Order is the cutover ladder:
// boring first, the per-call ledger last.
const STREAMS: { stream: EventStream; label: string; blurb: string }[] = [
  {
    stream: 'sdr_pool_heartbeats',
    label: 'Seat heartbeats',
    blurb: 'The 30s per-seat pulse behind pool time. A lost tick is superseded by the next.',
  },
  {
    stream: 'call_stages',
    label: 'Call stages',
    blurb: 'Per-call funnel checkpoints for the dashboards.',
  },
  {
    stream: 'pool_events',
    label: 'Pool events',
    blurb: 'Joins, leaves, opens — the pay pairing’s source table.',
  },
  {
    stream: 'call_events',
    label: 'Call events',
    blurb: 'The per-call ledger every cost and dial count reads. Flips last.',
  },
];

const transportOf = (
  map: Partial<Record<EventStream, EventTransport>> | undefined,
  stream: EventStream,
): EventTransport => (map?.[stream] === 'queue' ? 'queue' : 'direct');

export const countQueued = (
  map: Partial<Record<EventStream, EventTransport>> | undefined,
): number => EVENT_STREAMS.filter((s) => transportOf(map, s) === 'queue').length;

const fullChoice = (
  map: Partial<Record<EventStream, EventTransport>> | undefined,
): Record<EventStream, EventTransport> =>
  Object.fromEntries(EVENT_STREAMS.map((s) => [s, transportOf(map, s)])) as Record<
    EventStream,
    EventTransport
  >;

// Editor for the event-transport flip. Validation lives server-side — an unknown
// stream/value 422s and the message is rendered verbatim here.
export function EventTransportModal({
  onClose,
  onLoaded,
  onSaved,
}: {
  onClose: () => void;
  onLoaded: (map: Partial<Record<EventStream, EventTransport>>) => void;
  onSaved: (map: Partial<Record<EventStream, EventTransport>>) => void;
}) {
  const dialog = useModalDismiss(onClose);
  const [saved, setSaved] = useState<Partial<Record<EventStream, EventTransport>> | null>(null);
  const [choice, setChoice] = useState<Record<EventStream, EventTransport> | null>(null);
  const [status, setStatus] = useState<string>('Loading…');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    fetchDialerSettings()
      .then((r) => {
        if (!live) return;
        const map = r.settings.event_transport;
        setSaved(map);
        setChoice(fullChoice(map));
        setStatus('');
        onLoaded(map);
      })
      .catch((err: unknown) => {
        if (live) setStatus(err instanceof AdminRequestError ? err.message : 'Failed to load');
      });
    return () => {
      live = false;
    };
  }, []);

  const submit = useCallback(
    (transport: Partial<Record<EventStream, EventTransport>>) => {
      setSaving(true);
      setError(null);
      updateEventTransport(transport)
        .then((r) => onSaved(r.settings.event_transport))
        .catch((err: unknown) => {
          setError(err instanceof AdminRequestError ? err.message : 'Failed to save');
          setSaving(false);
        });
    },
    [onSaved],
  );

  const save = useCallback(() => {
    if (!saved || !choice) return;

    // Queue is the one direction worth a pause: from the next event write the
    // flipped streams ride SQS. Reverting needs NO friction — the red button
    // below is the brake, and a brake must not ask twice. The wording is
    // careful (codex P2, #659): only a DEFINITE send failure falls back to
    // the direct write; an ambiguous one (timeout/5xx) retries once and then
    // drops that batch — counted and alerted — never a direct write that
    // could double-count the cost/dial readers.
    const flipping = EVENT_STREAMS.filter(
      (s) => choice[s] === 'queue' && transportOf(saved, s) !== 'queue',
    );
    if (
      flipping.length > 0 &&
      !window.confirm(
        `Route ${flipping.join(', ')} through the SQS event queue?\n\nTakes effect on the next event write — no deploy. Rows arrive ~1–2s later than direct. A send that definitely failed falls back to the direct write and alerts; an ambiguous failure (timeout) retries once, then drops that batch — counted and alerted — rather than risk double-counting. Reverting is instant from this same switch. Continue?`,
      )
    ) {
      return;
    }

    // Stale-save guard (codex P1, #659): the endpoint replaces the map
    // WHOLESALE, so a Save from a modal opened before someone else's edit —
    // the panic revert especially — would silently re-serialize the old
    // world. Re-read first; if the live map moved underneath this modal,
    // refuse, reset to reality, and make the operator re-state their intent.
    // The brake below skips this on purpose: {} is absolute and cannot be
    // stale.
    setSaving(true);
    setError(null);
    fetchDialerSettings()
      .then((r) => {
        const fresh = r.settings.event_transport;
        if (!transportMapsEqual(fresh, saved)) {
          setSaved(fresh);
          setChoice(fullChoice(fresh));
          setSaving(false);
          setError(
            'The transport changed while this dialog was open — selection reset to what is live now. Re-apply if still wanted.',
          );
          return;
        }

        // Only queue entries are sent: absent = direct, so all-direct is {} —
        // the same canonical shape the panic button writes.
        submit(
          Object.fromEntries(
            EVENT_STREAMS.filter((s) => choice[s] === 'queue').map((s) => [s, 'queue' as const]),
          ),
        );
      })
      .catch((err: unknown) => {
        setError(err instanceof AdminRequestError ? err.message : 'Failed to save');
        setSaving(false);
      });
  }, [saved, choice, submit]);

  const dirty =
    saved !== null &&
    choice !== null &&
    EVENT_STREAMS.some((s) => choice[s] !== transportOf(saved, s));
  const anyQueuedSaved =
    saved !== null && EVENT_STREAMS.some((s) => transportOf(saved, s) === 'queue');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        {...dialog}
        aria-label="Event transport"
        className="bg-card flex max-h-[90vh] w-[min(30rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold">Event transport</h2>
          <p className="text-muted-foreground text-sm">
            Which path each ClickHouse event stream writes through. Direct is today’s in-process
            insert; queue rides SQS with a monitored direct fallback. Takes effect on the next write
            — no deploy.
          </p>
        </div>

        {status && <p className="text-muted-foreground text-sm">{status}</p>}
        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {error}
          </div>
        )}

        {saved && choice && (
          <>
            <div className="flex flex-col gap-2">
              {STREAMS.map(({ stream, label, blurb }) => (
                <div
                  key={stream}
                  className={cn(
                    'flex items-start justify-between gap-3 rounded-md border p-3',
                    choice[stream] === 'queue' && 'border-foreground/40 bg-muted/40',
                  )}
                >
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {label}
                      <span className="text-muted-foreground text-xs font-normal">{stream}</span>
                      {transportOf(saved, stream) === 'queue' && (
                        <span className="text-muted-foreground text-xs font-normal">
                          (on queue)
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground block text-xs leading-relaxed">
                      {blurb}
                    </span>
                  </span>
                  <div
                    className="flex shrink-0 overflow-hidden rounded-md border"
                    role="radiogroup"
                    aria-label={`${stream} transport`}
                  >
                    {(['direct', 'queue'] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        role="radio"
                        aria-checked={choice[stream] === t}
                        className={cn(
                          'px-2.5 py-1 text-xs',
                          choice[stream] === t
                            ? t === 'queue'
                              ? 'bg-emerald-600 text-white'
                              : 'bg-foreground text-background'
                            : 'text-muted-foreground hover:bg-muted/60',
                        )}
                        onClick={() => setChoice({ ...choice, [stream]: t })}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-2">
              {/* The brake: everything back to today's path in one click, no
                  confirmation — reverting is always safe by construction. */}
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => submit({})}
                disabled={saving || !anyQueuedSaved}
              >
                Everything → direct
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" onClick={save} disabled={!dirty || saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
